import { gql, MutationHookOptions, QueryHookOptions, useApolloClient, useMutation, useQuery } from '@apollo/client'
import { useCallback } from 'react'
import {
  CatalogPart,
  CatalogPartSortFieldEnum,
  Mutation,
  MutationCreateCatalogPartsArgs,
  MutationExportCatalogPartsArgs,
  MutationUploadCatalogArgs,
  OrderDirection,
  Query,
  QueryCatalogPartsArgs,
} from '../gen/models'

export const CATALOG_PARTS_QUERY = gql`
  query catalogParts(
    $input: GetCatalogPartsByCriteriaInput!
    $pagination: Pagination
    $sorts: [CatalogPartsSortInput!]
  ) {
    catalogParts(input: $input, pagination: $pagination, sorts: $sorts) {
      totalHits
      hits {
        tsId
        vendor
        vendorItem
        manufacturer
        manufacturerItem
        description
        category
        subCategory
      }
    }
  }
`

export const useCatalogPartsQuery = (
  baseOptions: QueryHookOptions<Pick<Query, 'catalogParts'>, QueryCatalogPartsArgs>,
) => useQuery(CATALOG_PARTS_QUERY, baseOptions)

export const UPLOAD_CATALOG = gql`
  mutation uploadCatalog($input: UploadCatalogInput!) {
    uploadCatalog(input: $input)
  }
`

export const useUploadCatalogMutation = (
  options?: MutationHookOptions<Pick<Mutation, 'uploadCatalog'>, MutationUploadCatalogArgs>,
) => useMutation(UPLOAD_CATALOG, options)

export const CREATE_CATALOG_PARTS = gql`
  mutation createCatalogParts($input: [CreateCatalogPartInput!]!) {
    createCatalogParts(input: $input) {
      tsId
    }
  }
`

export const useCreateCatalogPartsMutation = (
  options?: MutationHookOptions<Pick<Mutation, 'createCatalogParts'>, MutationCreateCatalogPartsArgs>,
) => useMutation(CREATE_CATALOG_PARTS, options)

// ─── Crosses EDIT pickers (X5 / #7333) ────────────────────────────────────────
// Two additive, imperative typeahead callbacks for the edit dialog (spec §1.4). Documents are
// exported consts for the recording-link suites. Both use the useSupplierCrossesTypeaheadTerms
// idiom: useApolloClient().query, network-only, consumed with a monotonic stale-guard in the cell.

// Cross-item picker — rides the SAME `catalogParts` op the #7292 listing uses (server forces
// vendorCatalog + catalogUpload); a distinct operation name because `catalogParts` is taken
// (SD-14). `partTiers { tiersId price }` feeds the label's lowest-tier-price segment (#7883 R1).
export const CATALOG_PARTS_CROSS_PICKER_QUERY = gql`
  query catalogPartsCrossPicker(
    $input: GetCatalogPartsByCriteriaInput!
    $pagination: Pagination
    $sorts: [CatalogPartsSortInput!]
  ) {
    catalogParts(input: $input, pagination: $pagination, sorts: $sorts) {
      totalHits
      hits {
        tsId
        manufacturer
        manufacturerItem
        description
        gmdn
        gicName
        typeOneName
        typeTwoName
        partTiers {
          tiersId
          price
        }
      }
    }
  }
`

// ─── Export Catalog (#7328) ──────────────────────────────────────────────────
// Queued job server-side, BLOCKING from the client's view: the resolver enqueues, waits in-request
// up to 120s, and returns a presigned downloadUrl (TTL 3600s). NO job id reaches the client —
// nothing to poll or subscribe to (discovery §4.2). Document exported for the recording-link
// page suite (react-conventions §14.1). Verbatim Appian selection (discovery §4.1).
export const EXPORT_CATALOG_PARTS = gql`
  mutation exportCatalogParts($input: ExportCatalogPartsInput!) {
    exportCatalogParts(input: $input) {
      downloadUrl
      s3Key
    }
  }
`

export const useCatalogPartsCrossPickerSearch = (): ((typedText: string) => Promise<CatalogPart[]>) => {
  const client = useApolloClient()
  return useCallback(
    async (typedText: string): Promise<CatalogPart[]> => {
      const { data } = await client.query<Pick<Query, 'catalogParts'>>({
        query: CATALOG_PARTS_CROSS_PICKER_QUERY,
        variables: {
          input: { searchText: typedText },
          pagination: { limit: 50, offset: 0 },
          sorts: [
            { field: CatalogPartSortFieldEnum.Manufacturer, direction: OrderDirection.Asc },
            { field: CatalogPartSortFieldEnum.ManufacturerItem, direction: OrderDirection.Asc },
          ],
        },
        fetchPolicy: 'network-only',
      })
      return data?.catalogParts?.hits ?? []
    },
    [client],
  )
}

// Submitted-side Edit — dependent Item picker. SD-8 traced wire: NO pagination variable, and
// `sort: { field: manufacturerItem }` with NO direction — the server default page {offset:0,limit:25}
// applies (byte-identical to Appian). Do NOT add pagination or a sort direction.
export const CATALOG_SEARCH_QUERY = gql`
  query catalogSearch($input: CatalogSearchInput!, $sort: CatalogPartsSortInput!) {
    catalogSearch(input: $input, sort: $sort) {
      totalHits
      hits {
        tsId
        manufacturer
        manufacturerItem
        description
      }
    }
  }
`

export const useCatalogSearch = (): ((normalizedManufacturer: string, typedText: string) => Promise<CatalogPart[]>) => {
  const client = useApolloClient()
  return useCallback(
    async (normalizedManufacturer: string, typedText: string): Promise<CatalogPart[]> => {
      const { data } = await client.query<Pick<Query, 'catalogSearch'>>({
        query: CATALOG_SEARCH_QUERY,
        variables: {
          input: { normalizedManufacturer, searchText: typedText },
          sort: { field: CatalogPartSortFieldEnum.ManufacturerItem },
        },
        fetchPolicy: 'network-only',
      })
      return data?.catalogSearch?.hits ?? []
    },
    [client],
  )
}

export const useExportCatalogPartsMutation = (
  options?: MutationHookOptions<Pick<Mutation, 'exportCatalogParts'>, MutationExportCatalogPartsArgs>,
) => useMutation(EXPORT_CATALOG_PARTS, options)
